/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ObjectId } from "bson";
import { AnimateSharedLayout, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  CategoriesAtom,
  CategoriesSearchAtom,
  IsCheckedAllCategoriesAtom,
} from "../../../atoms/CategoriesAtom";
import {
  NumberOfTagsInCategoryAtom,
  SelectedCategoriesAtom,
} from "../../../atoms/SelectedCategoriesAtom";
import { TagsAtom } from "../../../atoms/TagsAtom";
import { UpdateCategoryIRoute } from "../../../generated/Api";
import useClickOutside from "../../../hook/useClickOutside";
import useRowsPerPage, { ITableName } from "../../../hook/useRowsPerPage";
import { notSelect } from "../../../styles/general";
import { ICampaignStatus } from "../../../types/CampaignTypes";
import StyledCheckBox from "../../StyledCheckBox";
import {
  getComparator,
  Order,
  stableSort,
  TableFooter,
} from "../../StyledTable";
import CategoriesTableRow from "./CategoriesTableRow";

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-y: scroll;
  padding: 20px;

  height: calc(100vh - 145px);
`;

const TableCard = styled.div`
  width: 100%;

  border: 1px solid #eaeaeabf;
  background-color: #ffffff;

  display: grid;
  grid-template-columns:
    100px minmax(120px, 160px) 60px minmax(110px, 180px)
    60px;
  grid-column-gap: 24px;

  padding: 15px 25px;

  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: 685px) {
    width: fit-content;
  }
`;

const TableHead = styled.div`
  color: #707070;
  display: flex;
  font-size: 14px;
  align-items: center;
  font-weight: 600;
  cursor: default;
  position: relative;
  ${notSelect}
`;

const TableActions = styled.div`
  color: #707070;
  display: flex;
  font-size: 14px;
  align-items: center;
  justify-content: flex-end;
  font-weight: 600;
  cursor: default;
`;

const Icon = styled(motion.img)`
  width: 10px;
  height: 10px;
  margin-left: 8px;
  ${notSelect}
  cursor: pointer;
`;

const marginRight30 = css`
  margin-right: 30px;
`;

const FlexRow = styled(motion.div)`
  display: flex;
  align-items: center;
  cursor: pointer;
  :hover {
    opacity: 0.6;
  }
`;

const NeedsToImplementSort = css`
  cursor: default;
  :hover {
    opacity: 1;
  }
`;

const WrapperCheckboxAndTitle = styled.div`
  display: flex;
  align-content: center;
  justify-content: start;
`;

const SelectPopover = styled.div`
  position: absolute;
  background: white;
  z-index: 20;
  height: fit-content;
  width: 240px;
  overflow: auto;
  border-radius: 5px;
  top: 15;
  box-shadow: 0px 3px 20px #00000026;
  border: 1px solid #d4d4d4;
  border-bottom: none;
  border-top: none;
  display: flex;
  flex-direction: column;
`;

const TitlePopover = styled.div`
  font-size: 13px;
  color: #888888;
  margin-bottom: 5px;
  margin-left: 17px;
  padding-top: 18px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  font-size: 14px;
  color: #2f2f2f;
  cursor: pointer;

  span {
    margin-left: 7px;
  }

  &:hover {
    background-color: #fb972e;
    color: #ffffff;
  }
`;

const divider = css`
  border-bottom: 1px solid #eaeaea;
`;
export interface FilterOptions {
  status: ICampaignStatus[];
  labels: string[];
  noLabels: boolean;
}

export default function CategoriesTable(): JSX.Element {
  let categoriesPool = useRecoilValue(CategoriesAtom);
  let tagsPool = useRecoilValue(TagsAtom);

  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof UpdateCategoryIRoute>("name");
  const categoriesSearch = useRecoilValue(CategoriesSearchAtom);
  const [openDropView, setOpenDropView] = useState(false);
  const selectCheckboxRef = useClickOutside(() => setOpenDropView(false));
  const [numberOfTags, setNumberOfTags] = useRecoilState(
    NumberOfTagsInCategoryAtom
  );
  const [selectedCategories, setSelectedCategories] = useRecoilState(
    SelectedCategoriesAtom
  );
  const [categoriesRowsInPage, setCategoriesRowsInPage] = useState([]);
  const [cachedRowsPerPage, updateCachedRowsPerPage] = useRowsPerPage(
    ITableName.CATEGORIES
  );
  const [isCheckedCategoriesOnPage, setIsCheckedCategoriesOnPage] =
    useState(false);
  const [isCheckedAllCategories, setIsCheckedAllCategories] = useRecoilState(
    IsCheckedAllCategoriesAtom
  );

  const [totalCategoriesFromFilter, setTotalCategoriesFromFilter] = useState(
    categoriesPool.length
  );

  useEffect(() => {
    if (
      !tagsPool ||
      tagsPool.length === 0 ||
      Object.keys(numberOfTags).length > 1
    )
      return;
    tagsPool.map((tag) => {
      setNumberOfTags((nt: { categoryId: string }) => {
        let updatedObj = { ...nt };
        updatedObj[tag.categoryId] = (nt[tag.categoryId] || 0) + 1;
        return updatedObj;
      });
    });
  }, [tagsPool]);

  useEffect(() => {
    setPage(0);
    setSelectedCategories([]);
    setIsCheckedAllCategories(false);

    return () => {
      setPage(0);
      setSelectedCategories([]);
      setIsCheckedAllCategories(false);
    };
  }, []);

  useEffect(() => {
    if (categoriesPool.length) {
      let categoriesFiltered = categoriesPool;

      if (categoriesSearch.trim() !== "") {
        categoriesFiltered = categoriesPool.filter((tp) =>
          tp.name
            .toLowerCase()
            .trim()
            .includes(categoriesSearch.trim().toLowerCase())
        );
      }

      let rowsInPage = stableSort(
        categoriesFiltered.map((c) => ({
          ...c,
          name: c.name,
          _id: c._id || "",
          createdAt: new Date(),
          usersTagged: 0,
          type: c.type || "",
        })),
        getComparator(order, orderBy)
      )
        .slice(
          page * (cachedRowsPerPage || rowsPerPage),
          page * (cachedRowsPerPage || rowsPerPage) +
            (cachedRowsPerPage || rowsPerPage)
        )
        .map((row) => {
          let rowWithoutLowercase = categoriesFiltered
            .map((c) => ({
              ...c,
              name: c.name,
              type: c.type || "",
              _id: c._id || "",
              createdAt: new ObjectId(c._id).getTimestamp(),
              usersTagged: 0,
              tagsAmount: numberOfTags[c._id] || 0,
            }))
            .find((r) => r._id === row._id) as typeof row;
          return rowWithoutLowercase;
        });
      setCategoriesRowsInPage(rowsInPage);
      setTotalCategoriesFromFilter(categoriesFiltered.length);
    }
  }, [
    page,
    rowsPerPage,
    order,
    orderBy,
    categoriesPool,
    categoriesSearch,
    numberOfTags,
  ]);

  const handleRequestSort = (property: keyof UpdateCategoryIRoute) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  function handlePage(vle: number) {
    setPage(vle);
    setSelectedCategories([]);
    setIsCheckedCategoriesOnPage(false);
  }

  function handleCheck(id: string) {
    setSelectedCategories((elems) => {
      if (elems.includes(id)) {
        return elems.filter((check) => check !== id);
      }
      return [...elems, id];
    });
  }

  function unselectAll() {
    setSelectedCategories([]);
    setIsCheckedCategoriesOnPage(false);
    setIsCheckedAllCategories(false);
    setOpenDropView(false);
  }

  function selectAllCategories() {
    const filteredCategories = categoriesSearch
      ? categoriesPool.filter((cp) =>
          cp.name
            .toLowerCase()
            .trim()
            .includes(categoriesSearch.trim().toLowerCase())
        )
      : categoriesPool;

    setSelectedCategories(
      filteredCategories.map((category) => {
        return category._id;
      })
    );
    setOpenDropView(false);
    setIsCheckedCategoriesOnPage(false);
    setIsCheckedAllCategories(true);
  }

  useEffect(() => {
    if (isCheckedAllCategories) selectAllCategories();
  }, [isCheckedAllCategories, page, categoriesPool]);

  return (
    <TableContainer>
      <TableCard>
        <WrapperCheckboxAndTitle>
          <div style={{ position: "relative" }}>
            <StyledCheckBox
              customCss={css`
                ${marginRight30}
                ${notSelect}
              `}
              check={isCheckedAllCategories || isCheckedCategoriesOnPage}
              handleCheck={() => setOpenDropView(!openDropView)}
            />
            {openDropView && categoriesPool && (
              <SelectPopover ref={selectCheckboxRef}>
                <TitlePopover>SELECT</TitlePopover>

                {!isCheckedCategoriesOnPage && (
                  <Row
                    onClick={() => {
                      setIsCheckedAllCategories(false);
                      setIsCheckedCategoriesOnPage(true);
                      setOpenDropView(!openDropView);
                      setSelectedCategories(categoriesRowsInPage);
                    }}
                    css={isCheckedAllCategories && divider}
                  >
                    <span>
                      {categoriesRowsInPage.length > 1
                        ? `Select ${categoriesRowsInPage.length} categories on this page`
                        : `Select ${categoriesRowsInPage.length} category on this page`}
                    </span>
                  </Row>
                )}

                {!isCheckedAllCategories && (
                  <Row
                    onClick={() => {
                      selectAllCategories();
                    }}
                    css={divider}
                  >
                    <span>
                      {totalCategoriesFromFilter > 1
                        ? `Select all ${totalCategoriesFromFilter} categories`
                        : `Select all ${totalCategoriesFromFilter} category`}
                    </span>
                  </Row>
                )}

                {selectedCategories.length >= 1 && (
                  <Row onClick={unselectAll}>
                    <span>Deselect all</span>
                  </Row>
                )}
              </SelectPopover>
            )}
          </div>
          <TableHead>
            <FlexRow onClick={() => handleRequestSort("type")}>
              TYPE
              <Icon
                src={"./icons/contacts/arrow.svg"}
                whileTap={{ scale: 0.8 }}
              />
            </FlexRow>
          </TableHead>
        </WrapperCheckboxAndTitle>

        <TableHead>
          <FlexRow onClick={() => handleRequestSort("name")}>
            CATEGORY
            <Icon
              src={"./icons/contacts/arrow.svg"}
              whileTap={{ scale: 0.8 }}
            />
          </FlexRow>
        </TableHead>

        <TableHead>
          <FlexRow css={NeedsToImplementSort}>TAGS</FlexRow>
        </TableHead>

        <TableHead>
          <FlexRow onClick={() => handleRequestSort("_id")}>
            DATE CREATED
            <Icon
              src={"./icons/contacts/arrow.svg"}
              whileTap={{ scale: 0.8 }}
            />
          </FlexRow>
        </TableHead>

        <TableActions>ACTIONS</TableActions>
      </TableCard>
      <AnimateSharedLayout>
        {categoriesRowsInPage.map((row) => {
          return (
            <CategoriesTableRow
              key={row._id}
              row={row as any}
              isChecked={selectedCategories.includes(row._id)}
              handleCheck={() => handleCheck(row._id)}
            />
          );
        })}
      </AnimateSharedLayout>
      <TableFooter
        page={page}
        length={
          categoriesSearch
            ? totalCategoriesFromFilter
            : categoriesPool.length || 0
        }
        rowsPerPage={cachedRowsPerPage || rowsPerPage}
        setRowsPerPage={(vle: number) => {
          setRowsPerPage(vle);
          updateCachedRowsPerPage(vle);
        }}
        handlePage={handlePage}
      />
    </TableContainer>
  );
}
